/**
 * 採用ページ「In Numbers」セクションの数字アニメーション
 * スクロールで画面に表示されたタイミングでアニメーション開始
 *
 * 2パターン（クラスで切り替え）:
 * - --animation-odometer: スロット風（数字が上に消えて下から次の数字が来る）
 * - --animation-pop: 隠れた場所からシュンッと表示
 */

const SECTION_SELECTOR = '.page-recruit-in-numbers';
const NUMBER_SELECTOR = '.page-recruit-in-numbers__list__item__value__number';
const SLOT_LENGTH = 10; // 20個の数字で回す
const SLOT_DURATION = 1200; // 固定時間で同じスピード感
const SLIDE_DURATION = 0; // スライドインと同時にスロット回転開始
const ITEM_DELAY = 100; // リストアイテムごとのディレイ（ms）
const THRESHOLD = 0.2;
const VISIBLE_MARGIN = 100; // このpx分表示されてからアニメーション開始
const RESIZE_DEBOUNCE = 150; // リサイズ時のデバウンス（ms）
const MOBILE_BREAKPOINT = 768; // この幅以下はスクロール発火なし・リサイズ時のみ

function isMobileView(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
}

/** 親の list__item のインデックスを取得 */
function getItemIndex(el: HTMLElement): number {
  const item = el.closest('.page-recruit-in-numbers__list__item');
  if (!item?.parentElement) return 0;
  return Array.from(item.parentElement.children).indexOf(item);
}

/**
 * スロット用の数字配列と目標インデックスを取得
 * - 整数: 20以下は1→20で回し、21以上は(target-20)→target
 * - 小数: 1〜floor(target) に target を追加して目標値で止める（例: 9.4→[1,2,...,9,9.4]）
 */
function getSlotNumbers(target: number): { numbers: number[]; targetIndex: number } {
  const isDecimal = target % 1 !== 0;
  if (isDecimal) {
    const floorVal = Math.floor(target);
    const numbers: number[] = [];
    for (let i = 1; i <= floorVal; i++) numbers.push(i);
    numbers.push(target);
    return { numbers, targetIndex: numbers.length - 1 };
  }
  if (target <= SLOT_LENGTH) {
    const numbers = Array.from({ length: SLOT_LENGTH }, (_, i) => i + 1);
    return { numbers, targetIndex: target - 1 };
  }
  const start = target - SLOT_LENGTH;
  const numbers = Array.from({ length: SLOT_LENGTH + 1 }, (_, i) => start + i);
  return { numbers, targetIndex: SLOT_LENGTH };
}

/** easeOutExpo: 最初は速く、終わりでゆっくり */
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/** 数値テキストをパース */
function parseNumberText(
  text: string
): { type: 'single'; value: number } | { type: 'ratio'; left: number; right: number } | null {
  const trimmed = text.trim();

  const ratioMatch = trimmed.match(/^(\d+)[：:]\s*(\d+)$/);
  if (ratioMatch) {
    return {
      type: 'ratio',
      left: parseInt(ratioMatch[1], 10),
      right: parseInt(ratioMatch[2], 10),
    };
  }

  const num = parseFloat(trimmed.replace(/[^\d.-]/g, ''));
  if (!isNaN(num)) {
    return { type: 'single', value: num };
  }

  return null;
}

/** スロット風オドメーター: 数字が上に消えて下から次の数字が来る */
function createSlotOdometer(
  el: HTMLElement,
  parsed: { type: 'single'; value: number } | { type: 'ratio'; left: number; right: number },
  delay = 0
): void {
  const originalText = el.textContent?.trim() ?? '';
  const separator = originalText.includes('：') ? '：' : ':';
  const hasSmall = el.classList.contains('--small');

  const createSlot = (target: number): { element: HTMLElement; targetIndex: number } => {
    const { numbers, targetIndex } = getSlotNumbers(target);
    const windowEl = document.createElement('span');
    windowEl.className = 'in-numbers-slot__window';

    const stripEl = document.createElement('span');
    stripEl.className = 'in-numbers-slot__strip';

    numbers.forEach((n) => {
      const cell = document.createElement('span');
      cell.className = 'in-numbers-slot__cell';
      cell.textContent = n.toString();
      stripEl.appendChild(cell);
    });

    windowEl.appendChild(stripEl);
    return { element: windowEl, targetIndex };
  };

  el.classList.add('in-numbers-slot');
  el.textContent = '';

  const slideInner = document.createElement('span');
  slideInner.className = 'in-numbers-slot__slide';

  if (parsed.type === 'single') {
    const { element: slot, targetIndex } = createSlot(parsed.value);
    slideInner.appendChild(slot);
    el.appendChild(slideInner);

    const strip = slot.querySelector<HTMLElement>('.in-numbers-slot__strip')!;

    const startSlotRoll = () => {
      const cellHeight = strip.firstElementChild?.getBoundingClientRect().height ?? 96;
      const targetY = -targetIndex * cellHeight;
      const startTime = performance.now();

      const tick = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / SLOT_DURATION, 1);
        const eased = easeOutExpo(progress);
        const currentY = targetY * eased;
        strip.style.transform = `translate3d(0, ${currentY}px, 0)`;

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          strip.style.transform = `translate3d(0, ${targetY}px, 0)`;
        }
      };

      requestAnimationFrame(tick);
    };

    const run = () => {
      requestAnimationFrame(() => {
        el.classList.add('--animated');
        setTimeout(() => requestAnimationFrame(startSlotRoll), SLIDE_DURATION);
      });
    };
    delay > 0 ? setTimeout(run, delay) : run();
  } else {
    el.classList.add('in-numbers-slot--ratio');

    const { element: leftSlotEl, targetIndex: leftTargetIndex } = createSlot(parsed.left);
    const sep = document.createElement('span');
    sep.className = 'in-numbers-slot__separator';
    sep.textContent = separator;
    const { element: rightSlotEl, targetIndex: rightTargetIndex } = createSlot(parsed.right);

    slideInner.appendChild(leftSlotEl);
    slideInner.appendChild(sep);
    slideInner.appendChild(rightSlotEl);
    el.appendChild(slideInner);

    const animateSlot = (slotEl: HTMLElement, targetIndex: number) => {
      const strip = slotEl.querySelector<HTMLElement>('.in-numbers-slot__strip')!;

      const startAnimation = () => {
        const cellHeight = strip.firstElementChild?.getBoundingClientRect().height ?? 96;
        const targetY = -targetIndex * cellHeight;
        const startTime = performance.now();

        const tick = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / SLOT_DURATION, 1);
          const eased = easeOutExpo(progress);
          strip.style.transform = `translate3d(0, ${targetY * eased}px, 0)`;

          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      };

      requestAnimationFrame(startAnimation);
    };

    const run = () => {
      requestAnimationFrame(() => {
        el.classList.add('--animated');
        setTimeout(() => {
          animateSlot(leftSlotEl, leftTargetIndex);
          animateSlot(rightSlotEl, rightTargetIndex);
        }, SLIDE_DURATION);
      });
    };
    delay > 0 ? setTimeout(run, delay) : run();
  }

  if (hasSmall) el.classList.add('--small');
}

/** 隠れた場所からシュンッと表示（ラッパーで overflow を確実に） */
function animatePop(el: HTMLElement, delay = 0): void {
  const text = el.textContent ?? '';
  el.innerHTML = `<span class="in-numbers-pop__inner">${escapeHtml(text)}</span>`;
  el.classList.add('in-numbers-pop');
  const addAnimated = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('--animated');
      });
    });
  };
  delay > 0 ? setTimeout(addAnimated, delay) : addAnimated();
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** 要素にアニメーションを適用（odometer または pop） */
function runAnimationForElement(el: HTMLElement, isOdometer: boolean, delay: number): void {
  if (isOdometer) {
    const parsed = parseNumberText(el.textContent ?? '');
    if (parsed) createSlotOdometer(el, parsed, delay);
  } else {
    animatePop(el, delay);
  }
}

/** 要素を元の状態にリセット */
function resetElement(el: HTMLElement): void {
  const original = el.dataset.inNumbersOriginal ?? el.textContent ?? '';
  el.textContent = original;
  el.classList.remove('in-numbers-slot', 'in-numbers-pop', '--animated', 'in-numbers-slot--ratio');
  delete el.dataset.inNumbersAnimated;
  // --small は元のスタイルなので残す
}

/** アニメーションを切り替えて再実行 */
function switchAnimation(section: HTMLElement, targetAnimation: 'odometer' | 'pop'): void {
  const elements = section.querySelectorAll<HTMLElement>(NUMBER_SELECTOR);
  const isOdometer = targetAnimation === 'odometer';

  // 元のテキストを保存（初回は dataset にない場合がある）
  elements.forEach((el) => {
    if (!el.dataset.inNumbersOriginal) {
      el.dataset.inNumbersOriginal = el.textContent?.trim() ?? '';
    }
  });

  // リセット
  elements.forEach(resetElement);

  // セクションのクラスを切り替え
  section.classList.remove('--animation-odometer', '--animation-pop');
  section.classList.add(`--animation-${targetAnimation}`);

  // アニメーション再実行
  elements.forEach((el) => {
    el.dataset.inNumbersAnimated = 'true';
    const itemDelay = getItemIndex(el) * ITEM_DELAY;
    runAnimationForElement(el, isOdometer, itemDelay);
  });
}

export const odometerInNumbers = {
  init: () => {
    const section = document.querySelector<HTMLElement>(SECTION_SELECTOR);
    if (!section) return;

    const isOdometer = section.classList.contains('--animation-odometer');
    const isPop = section.classList.contains('--animation-pop');

    if (!isOdometer && !isPop) return;

    const elements = section.querySelectorAll<HTMLElement>(NUMBER_SELECTOR);
    if (elements.length === 0) return;

    // VISIBLE_MARGIN の可視化（デバッグ用）
    // const debugEl = document.createElement('div');
    // debugEl.className = 'in-numbers-visible-margin-debug';
    // debugEl.style.cssText = `
    //   position: fixed;
    //   bottom: 0;
    //   left: 0;
    //   width: 100%;
    //   height: ${VISIBLE_MARGIN}px;
    //   outline: 2px dashed rgba(255, 0, 0, 0.6);
    //   background: rgba(255, 0, 0, 0.05);
    //   pointer-events: none;
    //   z-index: 9999;
    //   box-sizing: border-box;
    // `;
    // debugEl.textContent = `VISIBLE_MARGIN: ${VISIBLE_MARGIN}px（この上でアニメーション発火）`;
    // debugEl.style.fontSize = '12px';
    // debugEl.style.color = 'rgba(255, 0, 0, 0.8)';
    // debugEl.style.padding = '4px 8px';
    // debugEl.style.fontFamily = 'sans-serif';
    // document.body.appendChild(debugEl);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target as HTMLElement;
          if (el.dataset.inNumbersAnimated === 'true') return;

          const originalText = el.textContent?.trim() ?? '';
          el.dataset.inNumbersOriginal = originalText;
          el.dataset.inNumbersAnimated = 'true';

          const itemDelay = getItemIndex(el) * ITEM_DELAY;
          runAnimationForElement(el, isOdometer, itemDelay);

          observer.unobserve(el);
        });
      },
      {
        threshold: THRESHOLD,
        rootMargin: `0px 0px -${VISIBLE_MARGIN}px 0px`,
      }
    );

    const runAllAnimations = () => {
      elements.forEach((el) => {
        if (!el.dataset.inNumbersOriginal) {
          el.dataset.inNumbersOriginal = el.textContent?.trim() ?? '';
        }
        resetElement(el);
        el.dataset.inNumbersAnimated = 'true';
        const itemDelay = getItemIndex(el) * ITEM_DELAY;
        runAnimationForElement(el, isOdometer, itemDelay);
      });
    };

    if (isMobileView()) {
      // スマホ: スクロールでは発火しない。セクションが初回表示されたとき1回だけ発火し、あとはリサイズ時のみ
      const sectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting) return;
          if (section.dataset.inNumbersSectionAnimated === 'true') return;
          section.dataset.inNumbersSectionAnimated = 'true';
          runAllAnimations();
          sectionObserver.disconnect();
        },
        { threshold: 0.1, rootMargin: `0px 0px -${VISIBLE_MARGIN}px 0px` }
      );
      sectionObserver.observe(section);
    } else {
      elements.forEach((el) => observer.observe(el));
    }

    // リサイズ時にアニメーションを再実行（レイアウト変更に対応）
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    window.addEventListener('resize', () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeTimeout = null;
        if (isMobileView()) {
          // スマホ: 幅が変わったときだけ発火（アドレスバー等の高さ変化でスクロール中に発火しない）
          const w = window.innerWidth;
          if (Math.abs(w - lastInnerWidth) > 10) {
            lastInnerWidth = w;
            delete section.dataset.inNumbersSectionAnimated;
            runAllAnimations();
          }
        } else {
          lastInnerWidth = window.innerWidth;
          elements.forEach((el) => {
            if (!el.dataset.inNumbersOriginal) {
              el.dataset.inNumbersOriginal = el.textContent?.trim() ?? '';
            }
            resetElement(el);
            observer.observe(el);
          });
        }
      }, RESIZE_DEBOUNCE);
    });

    // アニメーション切替ボタン
    const toggle = section.querySelector('.page-recruit-in-numbers__animation-toggle');
    if (toggle) {
      toggle.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest('[data-animation]');
        if (!btn || !(btn instanceof HTMLButtonElement)) return;
        const target = btn.dataset.animation as 'odometer' | 'pop';
        if (target !== 'odometer' && target !== 'pop') return;

        switchAnimation(section, target);

        toggle.querySelectorAll('.page-recruit-in-numbers__animation-toggle__btn').forEach((b) => b.classList.remove('--active'));
        btn.classList.add('--active');
      });
    }
  },
};
